import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, Wand2, Trash2 } from 'lucide-react';
import { MappingSetEditor } from '@/components/MappingSetEditor';
import { MappingDryRun } from '@/components/MappingDryRun';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const STATUS = { draft: C.amber, approved: C.green, superseded: C.slate };

function Proposer({ api, catalogue, runs, onProposed }) {
  const [runId, setRunId] = useState('');
  const [entity, setEntity] = useState('policy');
  const [client, setClient] = useState('');
  const [headers, setHeaders] = useState('');
  const [busy, setBusy] = useState(false);

  const propose = async () => {
    setBusy(true);
    try {
      const body = runId
        ? { run_id: runId, client }
        : { entity, client, headers: headers.split(',').map(h => h.trim()).filter(Boolean) };
      const { data } = await api.post('/migration/mappings', body);
      toast.success(`Draft proposed — ${data.rows.filter(r => r.proposed).length} of ${data.rows.length} fields matched a source column`);
      onProposed(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not propose a map');
    }
    setBusy(false);
  };

  const extracts = (runs || []).filter(r => r.scope === 'extract');

  return (
    <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}
      data-testid="proposer-card">
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>Propose a map</h3>
      <p className="text-[11px] mb-2" style={{ color: C.slate }}>
        Start from a profile run and the workbench proposes the obvious: header and alias matches, a value map
        for every spelling the profiler saw, and a workflow map from the lifecycle states in the data. You accept,
        override or waive each one — nothing is assumed silently.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-1.5 items-end">
        <div>
          <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>From a profile run</p>
          <select value={runId} onChange={e => setRunId(e.target.value)} data-testid="propose-run-select"
            className="w-full text-xs rounded-sm border px-2 h-8" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
            <option value="">— or list the columns below —</option>
            {extracts.map(r => (
              <option key={r.run_id} value={r.run_id}>
                {`${(r.entities || []).join(',')} · ${r.client || r.source || r.filename} · ${(r.finished_at || '').slice(0, 10)}`}
              </option>
            ))}
          </select>
        </div>
        {!runId && (
          <>
            <div>
              <p className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: C.slate }}>Canonical shape</p>
              <select value={entity} onChange={e => setEntity(e.target.value)} data-testid="propose-entity-select"
                className="w-full text-xs rounded-sm border px-2 h-8" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
                {(catalogue?.canonical?.entities || []).map(e2 => <option key={e2.entity} value={e2.entity}>{e2.label}</option>)}
              </select>
            </div>
            <Input value={headers} onChange={e => setHeaders(e.target.value)} data-testid="propose-headers-input"
              placeholder="Policy No, Insured, Product, …" className="text-xs rounded-sm h-8" />
          </>
        )}
        <Input value={client} onChange={e => setClient(e.target.value)} data-testid="propose-client-input"
          placeholder="Client / source system" className="text-xs rounded-sm h-8" />
        <Button size="sm" onClick={propose} disabled={busy || (!runId && !headers.trim())}
          className="text-xs rounded-sm h-8" style={{ background: C.navy }} data-testid="propose-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
          Propose draft
        </Button>
      </div>
    </Card>
  );
}

export function MappingWorkbench({ api, isAdmin, runs }) {
  const [catalogue, setCatalogue] = useState(null);
  const [sets, setSets] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSets = useCallback(() => api.get('/migration/mappings')
    .then(r => setSets(r.data.sets || [])).catch(() => { }), [api]);

  const openSet = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/migration/mappings/${id}`);
      setOpen(data);
    } catch { toast.error('Mapping set not found'); }
  }, [api]);

  useEffect(() => {
    Promise.all([api.get('/migration/mapping/catalogue'), api.get('/migration/mappings')])
      .then(([c, s]) => { setCatalogue(c.data); setSets(s.data.sets || []); })
      .catch(() => toast.error('Failed to load the workbench'))
      .finally(() => setLoading(false));
  }, [api]);

  const targetsFor = (entity, field) => {
    if (field === '__workflow__') {
      const wf = catalogue?.workflows?.[entity];
      return (wf?.states || []).map(s => ({
        value: s.state, label: `${s.label}${s.terminal ? ' (terminal)' : ''}`, starts: s.starts,
      }));
    }
    const shape = (catalogue?.canonical?.entities || []).find(e => e.entity === entity);
    const f = (shape?.fields || []).find(x => x.field === field);
    if (field === 'class') return (catalogue?.classes || []).map(c => ({ value: c.label, label: `${c.label} (${c.id})` }));
    return (f?.values || []).map(v => ({ value: v, label: v }));
  };

  const removeDraft = async (id) => {
    try {
      await api.delete(`/migration/mappings/${id}`);
      toast.success('Draft discarded');
      setOpen(null);
      loadSets();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not discard');
    }
  };

  if (loading) return <div className="text-sm p-6" style={{ color: C.slate }}>Loading the workbench…</div>;

  if (open) return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => { setOpen(null); loadSets(); }}
          className="text-[11px] h-6 px-2 rounded-sm" style={{ color: C.blue }} data-testid="back-to-sets-btn">
          <ChevronLeft className="w-3 h-3 mr-1" />All mapping sets
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-[11px]" style={{ color: C.slate }} data-testid="open-set-header">
            {open.client || 'unnamed client'} · {open.source_system} · <b style={{ color: C.navy }}>{open.label}</b> · v{open.version}
          </span>
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
            style={{ background: (STATUS[open.status] || C.slate) + '15', color: STATUS[open.status] || C.slate }}>
            {open.status}
          </span>
          {isAdmin && open.status === 'draft' && (
            <Button size="sm" variant="ghost" onClick={() => removeDraft(open.set_id)}
              className="h-6 px-1.5 text-[10px]" style={{ color: C.red }} data-testid="discard-draft-btn">
              <Trash2 className="w-3 h-3 mr-1" />Discard draft
            </Button>
          )}
        </div>
      </div>
      <MappingSetEditor api={api} set={open} transforms={catalogue?.transforms || []}
        targetsFor={targetsFor} onSaved={(d) => setOpen(d)} isAdmin={isAdmin} />
      <MappingDryRun api={api} set={open} outstanding={open.outstanding}
        onChanged={(newId) => openSet(newId || open.set_id)} isAdmin={isAdmin} />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="mapping-workbench">
      {isAdmin ? (
        <Proposer api={api} catalogue={catalogue} runs={runs} onProposed={(d) => { setOpen({ ...d, outstanding: [] }); loadSets(); openSet(d.set_id); }} />
      ) : (
        <p className="text-[11px] p-2 rounded-sm" style={{ background: 'rgba(136,150,168,0.08)', color: C.slate }}
          data-testid="workbench-read-only-note">
          Proposing and approving a mapping set is an admin action — agreed maps are readable here.
        </p>
      )}

      <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="sets-card">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.slate }}>Mapping sets</h3>
        {!sets.length ? (
          <p className="text-[11px]" style={{ color: C.slate }} data-testid="sets-empty">
            No map has been agreed yet. A migration is not scoped until one is.
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="sets-table">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Client</th><th className="py-1 pr-2">Source</th><th className="py-1 pr-2">Shape</th>
              <th className="py-1 pr-2">Version</th><th className="py-1 pr-2">Status</th>
              <th className="py-1 pr-2">Dry run</th><th className="py-1 pr-2">Approved by</th><th className="py-1">Created</th>
            </tr></thead>
            <tbody>
              {sets.map(s => (
                <tr key={s.set_id} onClick={() => openSet(s.set_id)} data-testid={`set-row-${s.set_id}`}
                  className="border-t cursor-pointer hover:bg-slate-50" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>{s.client || '—'}</td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>{s.source_system || '—'}</td>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>{s.label}</td>
                  <td className="py-1.5 pr-2 font-mono">v{s.version}</td>
                  <td className="py-1.5 pr-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
                      style={{ background: (STATUS[s.status] || C.slate) + '15', color: STATUS[s.status] || C.slate }}>
                      {s.superseded_by ? 'superseded' : s.status}
                    </span>
                  </td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: s.last_dry_run ? C.green : C.amber }}>
                    {s.last_dry_run ? `${s.last_dry_run.acceptance_pct}% of ${(s.last_dry_run.rows || 0).toLocaleString()}` : 'never run'}
                  </td>
                  <td className="py-1.5 pr-2 text-[10px]" style={{ color: C.slate }}>
                    {s.approved?.by?.name || s.approved?.by?.email || '—'}
                    {s.approved?.agreed_with ? ` · with ${s.approved.agreed_with}` : ''}
                  </td>
                  <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>{(s.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
